import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  styled,
  Alert,
  CircularProgress,
  Stack,
  useTheme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "../../../assets/images/logo.png";
import usePasswordReset from "../../../hooks/api-request/usePasswordReset";
import { useTranslation } from "react-i18next";
// Import visibility icons
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const AuthWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  backgroundColor: "#fff",
  overflow: "hidden",
}));

const AuthCard = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 450,
  margin: "auto",
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: -200,
    right: -200,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    zIndex: 0,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -200,
    left: -200,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    zIndex: 0,
  },

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const ContentBox = styled(Box)({
  width: "100%",
  position: "relative",
  zIndex: 1,
  backgroundColor: "#fff",
  borderRadius: 24,
  padding: "32px 24px",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
});

const LogoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 24,
  "& img": {
    height: 40,
  },
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: 16,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    backgroundColor: "#F5F5F5",
    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const BackToHomeLink = styled(Link)(({ theme }) => ({
  position: "absolute",
  top: 20,
  left: 20,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  textDecoration: "none",
  fontSize: "0.9rem",
  padding: "8px 16px",
  borderRadius: 20,
  transition: "all 0.2s ease",
  zIndex: 2,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.text.primary,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",

  "&:hover": {
    backgroundColor: "#fff",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

const ArrowIcon = styled("span")({
  display: "inline-block",
  marginRight: "4px",
  transition: "transform 0.2s ease",
});

// OTP Input styled component
const OtpInput = styled(TextField)(({ theme }) => ({
  width: "48px",
  height: "48px",
  margin: "0 4px",
  "& .MuiInputBase-input": {
    padding: "12px 0",
    textAlign: "center",
    fontSize: "1.25rem",
    fontWeight: "500",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    backgroundColor: "#F5F5F5",
    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const PASSWORD_REQUIREMENTS = [
  { rule: (v) => v.length >= 8, message: "length" },
  { rule: (v) => /[A-Z]/.test(v), message: "uppercase" },
  { rule: (v) => /[a-z]/.test(v), message: "lowercase" },
  { rule: (v) => /[0-9]/.test(v), message: "number" },
  {
    rule: (v) => /[^A-Za-z0-9]/.test(v),
    message: "special",
  },
];

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';

  // Create refs individually at the top level
  const otpRef1 = useRef(null);
  const otpRef2 = useRef(null);
  const otpRef3 = useRef(null);
  const otpRef4 = useRef(null);
  const otpRef5 = useRef(null);
  const otpRef6 = useRef(null);

  // Combine them into an array for easy access
  const otpRefs = [otpRef1, otpRef2, otpRef3, otpRef4, otpRef5, otpRef6];

  const [formData, setFormData] = useState({
    email: location.state?.email || "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Add state for individual OTP digits
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);

  const [error, setError] = useState("");

  const {
    verifyOTPAndResetPassword,
    resendOTP,
    loading,
    verifySuccess,
    verifyError,
    VerifySnackbar,
  } = usePasswordReset();

  // Add password visibility states
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Redirect if email is not provided
  useEffect(() => {
    if (!formData.email) {
      navigate("/forgot-password");
    }
  }, [formData.email, navigate]);

  // Redirect to login after successful password reset
  useEffect(() => {
    if (verifySuccess) {
      setTimeout(() => {
        navigate("/login", {
          state: {
            message:
              "Password reset successful. You can now login with your new password.",
          },
        });
      }, 2000);
    }
  }, [verifySuccess, navigate]);

  // Set error from backend
  useEffect(() => {
    if (verifyError) {
      setError(verifyError.message || "An error occurred during verification");
    }
  }, [verifyError]);

  // Update the combined OTP whenever individual digits change
  useEffect(() => {
    const combinedOtp = otpDigits.join("");
    setFormData((prev) => ({
      ...prev,
      otp: combinedOtp,
    }));
  }, [otpDigits]);

  const handleOtpChange = (e, index) => {
    const value = e.target.value;

    // Allow only single digit
    if (!/^\d?$/.test(value)) return;

    // Update the digit at the current index
    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = value;
    setOtpDigits(newOtpDigits);

    // If a digit was entered and not the last input, move focus to the next input
    if (value !== "" && index < 5) {
      otpRefs[index + 1].current.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    // Handle backspace to move to previous input
    if (e.key === "Backspace") {
      if (index > 0 && otpDigits[index] === "") {
        otpRefs[index - 1].current.focus();
      }
    }

    // Handle left arrow key
    if (e.key === "ArrowLeft" && index > 0) {
      otpRefs[index - 1].current.focus();
    }

    // Handle right arrow key
    if (e.key === "ArrowRight" && index < 5) {
      otpRefs[index + 1].current.focus();
    }
  };

  // Handle paste event for the OTP
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    // Only allow pasting numbers
    if (!/^\d+$/.test(pastedData)) return;

    const digits = pastedData.slice(0, 6).split("");
    const newOtpDigits = [...otpDigits];

    digits.forEach((digit, index) => {
      if (index < 6) {
        newOtpDigits[index] = digit;
      }
    });

    setOtpDigits(newOtpDigits);

    // Focus the next empty input or the last one if all are filled
    const nextEmptyIndex = newOtpDigits.findIndex((d) => d === "");
    if (nextEmptyIndex !== -1) {
      otpRefs[nextEmptyIndex].current.focus();
    } else if (digits.length < 6) {
      otpRefs[digits.length].current.focus();
    } else {
      otpRefs[5].current.focus();
    }
  };

  const validateForm = () => {
    if (formData.otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return false;
    }

    if (!formData.newPassword) {
      setError("Please enter a new password");
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    // Password validation
    const passwordValid = PASSWORD_REQUIREMENTS.every((req) =>
      req.rule(formData.newPassword),
    );

    if (!passwordValid) {
      setError("Please ensure your password meets all requirements");
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      verifyOTPAndResetPassword(formData);
    }
  };

  const handleResendOTP = () => {
    resendOTP(formData.email);
    // Reset OTP fields
    setOtpDigits(["", "", "", "", "", ""]);
    otpRefs[0].current.focus();
  };

  // Add toggle handlers
  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <AuthWrapper dir={isRtl ? 'rtl' : 'ltr'}>
      <BackToHomeLink to="/">
        <ArrowIcon>←</ArrowIcon>
        {t('auth.verifyOTP.backToHome')}
      </BackToHomeLink>

      <AuthCard>
        <ContentBox>
          <LogoContainer>
            <img src={Logo} alt="Logo" />
          </LogoContainer>

          <Typography variant="h5" align="center" gutterBottom fontWeight={600}>
            {t('auth.verifyOTP.title')}
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="textSecondary"
            sx={{ mb: 3 }}
          >
            {t('auth.verifyOTP.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {verifySuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('auth.verifyOTP.messages.success')}
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 1, fontWeight: 500 }}
            >
              {t('auth.verifyOTP.fields.otpTitle')}
            </Typography>

            {/* OTP Input Section */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
                width: "100%",
              }}
              onPaste={handleOtpPaste}
            >
              {otpDigits.map((digit, index) => (
                <OtpInput
                  key={index}
                  inputRef={otpRefs[index]}
                  value={digit}
                  onChange={(e) => handleOtpChange(e, index)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                  type="tel"
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                    pattern: "[0-9]",
                  }}
                  autoComplete="off"
                />
              ))}
            </Box>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 1, fontWeight: 500 }}
            >
              {t('auth.verifyOTP.fields.newPasswordTitle')}
            </Typography>

            <StyledTextField
              fullWidth
              placeholder={t('auth.verifyOTP.fields.newPassword')}
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={formData.newPassword}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleToggleNewPasswordVisibility}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ mt: 1, mb: 2 }}>
              {PASSWORD_REQUIREMENTS.map((req, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  sx={{
                    display: "block",
                    color: req.rule(formData.newPassword)
                      ? "success.main"
                      : "text.secondary",
                    "&::before": {
                      content: '"✓ "',
                      color: req.rule(formData.newPassword)
                        ? "success.main"
                        : "text.disabled",
                    },
                  }}
                >
                  {t(`auth.verifyOTP.passwordRequirements.${req.message}`)}
                </Typography>
              ))}
            </Box>

            <StyledTextField
              fullWidth
              placeholder={t('auth.verifyOTP.fields.confirmPassword')}
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2,
                mb: 2,
                height: 48,
                borderRadius: 2,
                position: "relative",
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              ) : (
                t('auth.verifyOTP.buttons.reset')
              )}
            </Button>
          </form>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="textSecondary">
              {t('auth.verifyOTP.resend.text')}
            </Typography>
            <Typography
              variant="body2"
              color="primary"
              sx={{
                cursor: "pointer",
                fontWeight: 500,
                textDecoration: "underline",
              }}
              onClick={handleResendOTP}
            >
              {t('auth.verifyOTP.resend.link')}
            </Typography>
          </Stack>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              {t('auth.verifyOTP.rememberPassword.text')}{" "}
              <Link
                to="/login"
                style={{
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                }}
              >
                {t('auth.verifyOTP.rememberPassword.link')}
              </Link>
            </Typography>
          </Box>
        </ContentBox>
      </AuthCard>

      {VerifySnackbar}
    </AuthWrapper>
  );
};

export default VerifyOTP;
